import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Bell, X, CheckCircle2 } from 'lucide-react';

/**
 * Compact bell + dropdown for admin in-app notification trail.
 * Polls every 30s when sidebar is rendered.
 */
export default function AdminNotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () =>
    api.get('/admin/notifications', { params: { limit: 50 } })
       .then(r => setItems(r.data))
       .catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const unread = items.filter(i => !i.read).length;

  const markRead = async (id) => {
    await api.post(`/admin/notifications/${id}/read`);
    load();
  };
  const markAll = async () => {
    await api.post('/admin/notifications/read-all');
    load();
  };

  return (
    <>
      <button onClick={()=>setOpen(true)}
              className="relative flex items-center gap-2 px-2 py-2 text-sm text-fg hover:bg-[#F2EBDB] rounded-md w-full"
              data-testid="admin-bell">
        <Bell className="w-4 h-4" strokeWidth={1.6}/>
        <span>Notifications</span>
        {unread > 0 && (
          <span className="ml-auto bg-[#B83A3A] text-white text-[10px] rounded-full px-1.5 py-0.5 font-semibold" data-testid="bell-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/40" onClick={()=>setOpen(false)} data-testid="bell-modal">
          <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] mt-12 flex flex-col" onClick={e=>e.stopPropagation()}>
            <div className="p-4 border-b border-soft flex items-center justify-between">
              <h3 className="font-heading font-semibold">Admin notifications</h3>
              <div className="flex items-center gap-2">
                {unread > 0 && (
                  <button onClick={markAll} className="text-xs text-brand hover:underline" data-testid="bell-mark-all">
                    Mark all read
                  </button>
                )}
                <button onClick={()=>setOpen(false)} className="text-fg-muted hover:text-fg"><X className="w-5 h-5"/></button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {items.map(n => (
                <div key={n.notification_id}
                     className={`p-3 border-b border-soft flex items-start gap-3 ${n.read ? 'opacity-60' : ''}`}
                     data-testid={`bell-item-${n.notification_id}`}>
                  <div className="text-[10px] uppercase tracking-wider text-brand font-mono shrink-0 w-32 mt-0.5">
                    {n.kind.replaceAll('_', ' ')}
                  </div>
                  <div className="flex-1 text-sm">
                    <div>{n.message}</div>
                    <div className="text-[10px] text-fg-muted mt-0.5">
                      {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                  {!n.read && (
                    <button onClick={()=>markRead(n.notification_id)} className="text-brand p-1 shrink-0" data-testid={`bell-read-${n.notification_id}`} title="Mark read">
                      <CheckCircle2 className="w-4 h-4"/>
                    </button>
                  )}
                </div>
              ))}
              {!items.length && (
                <div className="text-center text-fg-muted py-8 text-sm">No notifications.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
